import { useState, type HTMLAttributes } from 'react';

import type { FigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import { Stars, type StarsRating } from '../Stars/Stars';
import { Tag } from '../Tag/Tag';
import './review-card.css';

export interface ReviewCardSlots {
  author: string;
  date: string;
  initials: string;
  rating: StarsRating;
  sourceLabel: string;
  text: string;
}

export interface ReviewCardProps
  extends Omit<HTMLAttributes<HTMLElement>, 'children'>,
    ReviewCardSlots {
  size: FigmaBreakpoint;
}

const FIGMA_NODES: Record<FigmaBreakpoint, { default: string; hover: string }> = {
  '1920': { default: '27:11443', hover: '186:51975' },
  '1440': { default: '27:11443', hover: '186:51975' },
  '768': { default: '125:38311', hover: '186:51991' },
  '375': { default: '125:39813', hover: '186:52007' },
};

export function ReviewCard({
  author,
  className = '',
  date,
  initials,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  rating,
  size,
  sourceLabel,
  text,
  ...props
}: ReviewCardProps) {
  const [interactive, setInteractive] = useState(false);
  const nodes = FIGMA_NODES[size];

  return (
    <article
      {...props}
      className={['lovo-review-card', `lovo-review-card--${size}`, className]
        .filter(Boolean)
        .join(' ')}
      data-figma-node={interactive ? nodes.hover : nodes.default}
      data-figma-variant={`Size=${size === '1440' ? '1920+1440' : size}, State=${interactive ? 'hover' : 'Default'}`}
      onBlur={(event) => {
        setInteractive(false);
        onBlur?.(event);
      }}
      onFocus={(event) => {
        setInteractive(true);
        onFocus?.(event);
      }}
      onMouseEnter={(event) => {
        setInteractive(true);
        onMouseEnter?.(event);
      }}
      onMouseLeave={(event) => {
        setInteractive(false);
        onMouseLeave?.(event);
      }}
      tabIndex={0}
    >
      <div className="lovo-review-card__content">
        <Tag className="lovo-review-card__tag" icon="no" label={sourceLabel} type="tag" />
        <div className="lovo-review-card__copy">
          <Stars className="lovo-review-card__stars" mode="Light" rating={rating} />
          <p>{text}</p>
        </div>
      </div>
      <footer className="lovo-review-card__footer">
        <div className="lovo-review-card__author">
          <span className="lovo-review-card__avatar" aria-hidden="true">{initials}</span>
          <span className="lovo-review-card__author-copy">
            <strong>{author}</strong>
            <time>{date}</time>
          </span>
        </div>
        <img className="lovo-review-card__quote" src="/figma/assets/review-quote.svg" alt="" aria-hidden="true" />
      </footer>
    </article>
  );
}
